import React, { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { PageHeader, Container } from "@/components/PageShell";
import { StatusBadge } from "@/components/Badges";
import { Plus, Trash2, Play, ArrowRight, Loader2, Globe, Flag, X } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full px-3 py-2 bg-white border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#FA3E05] focus:border-[#FA3E05]";
const btnPrimary = "bg-[#FA3E05] hover:bg-[#E03504] disabled:opacity-60 text-white text-sm font-medium rounded-md px-4 py-2 transition-colors inline-flex items-center gap-2";
const btnGhost = "hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900 text-sm rounded-md px-2.5 py-1.5 transition-colors inline-flex items-center gap-1.5";

function ChainForm({ relays, exits, onClose, onSaved }) {
    const [data, setData] = useState({ name: "", ru_relay_id: "", foreign_exit_id: "" });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true); setErr("");
        try {
            await api.post("/chains", data);
            toast.success("Цепочка создана");
            onSaved();
        } catch (e) { setErr(extractError(e)); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-zinc-950/30 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white border border-zinc-200 rounded-md shadow-xl w-full max-w-lg" data-testid="chain-form-modal">
                <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
                    <h2 className="text-base font-semibold">Новая маршрутная цепочка</h2>
                    <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-700"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Название</label>
                        <input required data-testid="chain-name-input" className={inputCls} value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} placeholder="Moscow → Amsterdam" />
                    </div>

                    {/* Visual builder */}
                    <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4">
                        <div className="flex items-center gap-3">
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium mb-1">Клиент</div>
                                <div className="text-xs font-mono text-zinc-600 bg-white border border-zinc-200 rounded px-2 py-1.5 truncate">user</div>
                            </div>
                            <ArrowRight className="w-4 h-4 text-zinc-400 mt-4 shrink-0" />
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium mb-1">RU Relay</div>
                                <select required data-testid="chain-relay-select" className={inputCls} value={data.ru_relay_id} onChange={(e) => setData({ ...data, ru_relay_id: e.target.value })}>
                                    <option value="">— выбрать —</option>
                                    {relays.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                            <ArrowRight className="w-4 h-4 text-zinc-400 mt-4 shrink-0" />
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium mb-1">Foreign Exit</div>
                                <select required data-testid="chain-exit-select" className={inputCls} value={data.foreign_exit_id} onChange={(e) => setData({ ...data, foreign_exit_id: e.target.value })}>
                                    <option value="">— выбрать —</option>
                                    {exits.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                            <ArrowRight className="w-4 h-4 text-zinc-400 mt-4 shrink-0" />
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium mb-1">Интернет</div>
                                <div className="text-xs font-mono text-zinc-600 bg-white border border-zinc-200 rounded px-2 py-1.5 truncate">internet</div>
                            </div>
                        </div>
                    </div>

                    {(relays.length === 0 || exits.length === 0) && (
                        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-md px-3 py-2">
                            Для создания цепочки нужны минимум один сервер с ролью <b>RU Relay</b> и один с ролью <b>Foreign Exit</b>.
                        </div>
                    )}

                    {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">{err}</div>}
                </div>
                <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2 bg-zinc-50">
                    <button type="button" onClick={onClose} className="bg-white border border-zinc-200 text-sm font-medium rounded-md px-4 py-2 hover:bg-zinc-50">Отмена</button>
                    <button type="submit" disabled={saving || relays.length === 0 || exits.length === 0} className={btnPrimary} data-testid="chain-form-submit">
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        Создать
                    </button>
                </div>
            </form>
        </div>
    );
}

export default function Chains() {
    const [chains, setChains] = useState([]);
    const [servers, setServers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newOpen, setNewOpen] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        try {
            const [c, s] = await Promise.all([api.get("/chains"), api.get("/servers")]);
            setChains(c.data); setServers(s.data);
        } catch (e) { toast.error(extractError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const apply = async (id) => {
        setBusyId(id);
        try {
            await api.post(`/chains/${id}/apply`);
            toast.success("Цепочка применена — идёт синхронизация серверов");
            load();
        } catch (e) { toast.error(extractError(e)); }
        finally { setBusyId(null); }
    };

    const del = async (id) => {
        if (!window.confirm("Удалить цепочку? Пользователи будут отвязаны.")) return;
        try {
            await api.delete(`/chains/${id}`);
            toast.success("Цепочка удалена");
            load();
        } catch (e) { toast.error(extractError(e)); }
    };

    const nameOf = (id) => servers.find((s) => s.id === id)?.name || "?";
    const hostOf = (id) => servers.find((s) => s.id === id)?.host || "";

    const relays = servers.filter((s) => s.role === "ru_relay");
    const exits = servers.filter((s) => s.role === "foreign_exit");

    return (
        <>
            <PageHeader
                title="Маршрутные цепочки"
                subtitle="Client → RU Relay → Foreign Exit → Internet"
                testid="chains-header"
                actions={
                    <button onClick={() => setNewOpen(true)} className={btnPrimary} data-testid="add-chain-btn">
                        <Plus className="w-4 h-4" /> Новая цепочка
                    </button>
                }
            />
            <Container>
                <div className="bg-white border border-zinc-200 rounded-md" data-testid="chains-list">
                    {loading ? (
                        <div className="p-10 text-center text-sm text-zinc-500">Загрузка…</div>
                    ) : chains.length === 0 ? (
                        <div className="p-14 text-center">
                            <div className="text-sm text-zinc-700 font-medium">Цепочек пока нет</div>
                            <div className="text-xs text-zinc-500 mt-1">Создайте цепочку: российский ретранслятор + зарубежный выход</div>
                        </div>
                    ) : (
                        <div className="divide-y divide-zinc-100">
                            {chains.map((c) => (
                                <div key={c.id} className="p-5" data-testid={`chain-row-${c.id}`}>
                                    <div className="flex items-start justify-between mb-3">
                                        <div>
                                            <div className="font-semibold text-sm">{c.name}</div>
                                            <div className="text-xs text-zinc-500 mt-0.5 font-mono">
                                                id: {c.id.slice(0, 8)}… · статус: <StatusBadge status={c.status} />
                                            </div>
                                        </div>
                                        <div className="flex gap-1">
                                            <button onClick={() => apply(c.id)} disabled={busyId === c.id} className={btnGhost} data-testid={`apply-chain-${c.id}`}>
                                                {busyId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Применить
                                            </button>
                                            <button onClick={() => del(c.id)} className={`${btnGhost} hover:!text-red-600`} data-testid={`delete-chain-${c.id}`}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Chain visualization */}
                                    <div className="flex items-center gap-2 overflow-auto">
                                        <div className="shrink-0 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2 min-w-[100px]">
                                            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Клиент</div>
                                            <div className="text-xs font-mono">user</div>
                                        </div>
                                        <ArrowRight className="w-4 h-4 text-zinc-400 shrink-0" />
                                        <div className="shrink-0 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2 min-w-[140px]">
                                            <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                                                <Flag className="w-2.5 h-2.5" /> RU Relay
                                            </div>
                                            <div className="text-xs font-medium">{nameOf(c.ru_relay_id)}</div>
                                            <div className="text-[10px] font-mono text-zinc-500">{hostOf(c.ru_relay_id)}</div>
                                        </div>
                                        <ArrowRight className="w-4 h-4 text-zinc-400 shrink-0" />
                                        <div className="shrink-0 bg-orange-50 border border-orange-200 rounded-md px-3 py-2 min-w-[140px]">
                                            <div className="text-[10px] uppercase tracking-wider text-orange-700 flex items-center gap-1">
                                                <Globe className="w-2.5 h-2.5" /> Foreign Exit
                                            </div>
                                            <div className="text-xs font-medium">{nameOf(c.foreign_exit_id)}</div>
                                            <div className="text-[10px] font-mono text-orange-600">{hostOf(c.foreign_exit_id)}</div>
                                        </div>
                                        <ArrowRight className="w-4 h-4 text-zinc-400 shrink-0" />
                                        <div className="shrink-0 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2 min-w-[100px]">
                                            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Интернет</div>
                                            <div className="text-xs font-mono">0.0.0.0/0</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="mt-4 text-xs text-zinc-500 bg-white border border-zinc-200 rounded-md p-4">
                    <b className="text-zinc-700">Как работает:</b> пользователь подключается к RU-серверу (российский IP в «белых списках»),
                    трафик маркируется и выходит через внутренний Reality-туннель к зарубежному серверу. Таким образом:
                    (1) провайдер видит только соединение с российским IP — не блокируется;
                    (2) во внешний мир трафик выходит с зарубежного IP — обходит гео-блокировки международных сервисов.
                </div>
            </Container>

            {newOpen && (
                <ChainForm
                    relays={relays} exits={exits}
                    onClose={() => setNewOpen(false)}
                    onSaved={() => { setNewOpen(false); load(); }}
                />
            )}
        </>
    );
}
